import { useMemo } from "react";
import { useTopographicMap } from "./TopographicMapContext";
import { useSvgViewBoxZoom } from "../../hooks/useSvgViewBoxZoom";

export function TopographicMapViewer() {
    const {
        viewBoxAttr,
        canvasW,
        canvasH,
        contourData,
        contourCount,
        fillByAltitude,
        fillColorForLevel,
        strokeColor,
        indexStrokeColor,
        bgColor,
        strokeWidth,
        indexStrokeWidthFactor,
        fillShapes,
        isIndexLevel,
        fieldRange,
        canExport,
        handleDownload,
    } = useTopographicMap();

    const base = useMemo(() => {
        const [x, y, w, h] = viewBoxAttr.split(" ").map(Number);
        return { x, y, w, h };
    }, [viewBoxAttr]);

    const {
        svgRef,
        viewBoxAttr: viewBox,
        panHandlers,
        zoomPercent,
        zoomIn,
        zoomOut,
        resetView,
    } = useSvgViewBoxZoom({ base });

    const totalPaths = contourData.reduce((n, l) => n + l.paths.length, 0);

    return (
        <div className="panel-viewer topographic-map-viewer">
            <div className="path-tool-preview">
                <div className="svg-zoom-controls">
                    <button
                        type="button"
                        className="zoom-button"
                        onClick={zoomOut}
                        title="Zoom out"
                    >
                        −
                    </button>
                    <span className="zoom-level">{zoomPercent}%</span>
                    <button
                        type="button"
                        className="zoom-button"
                        onClick={zoomIn}
                        title="Zoom in"
                    >
                        +
                    </button>
                    <button
                        type="button"
                        className="zoom-button"
                        onClick={resetView}
                        title="Reset zoom"
                    >
                        ⌂
                    </button>
                </div>

                <div className="svg-scroll-wrapper">
                    <div className="svg-container svg-container--fill">
                        <svg
                            ref={svgRef}
                            viewBox={viewBox}
                            xmlns="http://www.w3.org/2000/svg"
                            className="path-tool-svg"
                            style={{ touchAction: "none" }}
                            {...panHandlers}
                        >
                            <rect
                                x={0}
                                y={0}
                                width={canvasW}
                                height={canvasH}
                                fill={bgColor}
                            />

                            <g className="topo-contours">
                                {contourData.map(
                                    ({ paths, level, threshold }) => {
                                        const idx = isIndexLevel(level);
                                        const sw = idx
                                            ? strokeWidth *
                                              indexStrokeWidthFactor
                                            : strokeWidth;
                                        const sc = idx
                                            ? indexStrokeColor
                                            : strokeColor;
                                        const fillOpacity = fillShapes
                                            ? fillByAltitude
                                                ? 1
                                                : (level + 1) / contourCount
                                            : 0;
                                        return paths.map((d, i) => (
                                            <path
                                                key={`c${level}-${i}`}
                                                d={d}
                                                fill={
                                                    fillShapes
                                                        ? fillColorForLevel(
                                                              threshold,
                                                              level,
                                                          )
                                                        : "none"
                                                }
                                                fillOpacity={fillOpacity}
                                                stroke={sc}
                                                strokeWidth={sw}
                                            />
                                        ));
                                    },
                                )}
                            </g>
                        </svg>
                    </div>
                </div>

                <div className="path-tool-footer">
                    <p className="path-tool-meta">
                        {fieldRange
                            ? `Elevation range ${fieldRange.minV.toFixed(3)} – ${fieldRange.maxV.toFixed(3)} · `
                            : ""}
                        {totalPaths} path{totalPaths !== 1 ? "s" : ""} ·{" "}
                        {contourCount} contour
                        {contourCount !== 1 ? "s" : ""} · {canvasW} × {canvasH}
                    </p>
                    <button
                        type="button"
                        className="generic-button"
                        disabled={!canExport}
                        onClick={handleDownload}
                    >
                        Download result SVG
                    </button>
                </div>
            </div>
        </div>
    );
}
